import { useState } from 'react';
import type { DecodedParameterNode } from './decoders';
import { ParameterDecodedDataCard } from './ParameterDecodedDataCard';
import {
  ContractCallAccordion,
  ContractCallAccordionSummary,
  ContractCallAccordionDetails,
} from './ContractCallAccordian';
import { ContractTxDetailsHeader } from './ContractTxDetailsHeader';
import { UnableToDecodeTransactionAlert } from './UnableToDecodeTransactionAlert';
import { type NetworkType } from '@mls-wallet/shared-types';
import { getReadableSolidityName } from '@/lib/solidity-formatting';
import type { AddressBookExactEntry } from '@/types/address-book';

// Parameters with these names may have bytes data that represents internal transactions to decode
const paramsToDecode = [
  'data',
  '_data',
  'calls',
  '_calls',
  'calldatas',
  '_calldatas',
];

// Parameters with these names may be used to help decode internal transactions
const paramsUsed = [
  'to',
  '_to',
  'target',
  '_target',
  'targets',
  '_targets',
  'value',
  '_value',
  'values',
  '_values',
  'signature',
  '_signature',
  'signatures',
  '_signatures',
  'setupContracts',
];

interface GeneralizedContractCardProps {
  functionName: string;
  contractAddress: string;
  nativeTokenValue?: { value: string; decimals: number };
  networkId: NetworkType;
  parameters: DecodedParameterNode[];
  addressBookEntryByAddress?: Map<string, AddressBookExactEntry>;
}

/**
 * Component showing the decoded data of a transaction. This shows the direct parameters and their inputted values.
 * This is meant to be displayed when no other custom modes apply.
 * Mirrors self-custody implementation using separate accordion components.
 *
 * @param {GeneralizedContractCardProps} props
 * @returns {React.ReactElement}
 */
export function GeneralizedContractCard({
  functionName,
  contractAddress,
  nativeTokenValue,
  networkId,
  parameters,
  addressBookEntryByAddress,
}: GeneralizedContractCardProps): React.ReactElement {
  // Use this state to control the expansion of the accordion
  const [expanded, setExpanded] = useState<boolean>(true);

  return (
    <div className="w-full -mx-2">
      <ContractCallAccordion
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
      >
        <ContractCallAccordionSummary>
          <ContractTxDetailsHeader
            functionName={functionName}
            contractAddress={contractAddress}
            networkId={networkId}
            expanded={expanded}
            nativeTokenValue={nativeTokenValue ?? undefined}
          />
        </ContractCallAccordionSummary>
        <ContractCallAccordionDetails>
          {(() => {
            // Case: Not expanded - don't render anything
            // This prevents rendering details when not visible (performance optimization
            // from self-custody: avoid spamming address resolution requests for 500+ collapsed txs)
            if (!expanded) {
              return null;
            }

            // Case: No decoded data (should not happen for transaction builder, but defensive)
            if (!functionName) {
              return <UnableToDecodeTransactionAlert />;
            }

            // Case: No inputs for this function
            if (parameters.length === 0) {
              return (
                <div className="bg-zinc-100 rounded-[8px] px-3 py-2">
                  <p className="text-[12px] text-zinc-600">
                    There are no inputs to this function
                  </p>
                </div>
              );
            }

            return (
              <div className="flex flex-col gap-3 pt-0 w-full">
                {parameters.map(
                  (param: DecodedParameterNode, index: number) => {
                    // Case: This param displays an internal transaction that combines data from other params
                    let combinedParamNames: string[] = [];
                    if (
                      paramsToDecode.includes(param.name) &&
                      Boolean(param.valueDecoded)
                    ) {
                      combinedParamNames = parameters
                        .filter((p) => paramsUsed.includes(p.name))
                        .map((p) =>
                          getReadableSolidityName(p.name, 'Parameter')
                        ) as string[];
                    }

                    return (
                      <ParameterDecodedDataCard
                        key={index}
                        param={param}
                        networkId={networkId}
                        contractAddress={contractAddress}
                        combinedParamNames={combinedParamNames}
                        methodName={functionName}
                        {...(addressBookEntryByAddress
                          ? { addressBookEntryByAddress }
                          : {})}
                      />
                    );
                  }
                )}
              </div>
            );
          })()}
        </ContractCallAccordionDetails>
      </ContractCallAccordion>
    </div>
  );
}
